import { useState, type FormEvent } from "react";

// 💡 【重要】送信先のメールアドレスに書き換えてください（自動的にそこに届きます）
const SUBMIT_EMAIL = "info@anniversary-car-rental.com"; 
const SUBMIT_URL = `https://formsubmit.co/ajax/${SUBMIT_EMAIL}`;

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [responseMessage, setResponseMessage] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // ハニーポット（スパム対策）の検証
    const honeypot = (formData.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setStatus("success");
      setResponseMessage("お問い合わせありがとうございます。2営業日以内に担当者よりご連絡させて頂きます。");
      form.reset();
      return;
    }

    setStatus("loading");
    setResponseMessage("");

    try {
      // FormSubmitに送信するデータを作成（ハニーポット用の項目は除外）
      const submitData = new FormData();
      Array.from(formData.entries()).forEach(([key, value]) => {
        if (key !== "phone_alt") {
          submitData.append(key, value);
        }
      });

      // 念のため、FormSubmit側の自動返信やタイトルを設定する特殊フィールドを追加可能
      submitData.append("_subject", "【公式ホームページ】新規お問い合わせ");

      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { 
          "Accept": "application/json" 
        },
        body: submitData,
      });

      if (!res.ok) {
        throw new Error("送信エラーが発生しました");
      }

      const parsed = await res.json();

      // FormSubmitは成功時に { success: "true", message: "..." } を返します
      if (parsed.success === "true" || parsed.success === true) {
        setStatus("success");
        setResponseMessage("お問い合わせありがとうございます。2営業日以内に担当者よりご連絡させて頂きます。");
        form.reset();
      } else {
        setStatus("error");
        setResponseMessage("送信に失敗しました。お手数ですがお電話でお問い合わせください。");
      }
    } catch (error) {
      setStatus("error");
      setResponseMessage("送信に失敗しました。お手数ですがお電話でお問い合わせください。");
    }
  };

  return (
    <section id="contact" className="relative bg-foreground-950 text-background-50 py-24 md:py-32 px-6 overflow-hidden">
      <div className="relative z-10 max-w-[1100px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* 左カラム: 案内テキスト & 連絡先 */}
          <div className="lg:col-span-5">
            <p className="text-[10px] tracking-[0.5em] uppercase text-accent-300 mb-4">— Get in Touch —</p>
            <h2 className="font-heading text-4xl md:text-5xl leading-tight">
              撮影・イベント・<br />
              <span className="italic text-accent-300">法人様</span>の<br />
              ご依頼はこちら
            </h2>
            <p className="mt-6 text-sm text-background-50/80 leading-loose">
              お問い合わせいただきありがとうございます。以下の内容をご記入ください。2営業日以内にご返信いたします。
            </p>

            <div className="mt-10 space-y-4 text-sm">
              <a href="tel:+817069422157" className="flex items-center gap-3 hover:text-accent-300 cursor-pointer transition-colors">
                <div className="w-10 h-10 flex items-center justify-center bg-background-50/10 rounded-sm">
                  <i className="ri-phone-line text-accent-300"></i>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-background-50/60">Tel</p>
                  <p>070-6942-2157</p>
                </div>
              </a>
              <a href="mailto:info@anniversary-car-rental.com" className="flex items-center gap-3 hover:text-accent-300 cursor-pointer transition-colors">
                <div className="w-10 h-10 flex items-center justify-center bg-background-50/10 rounded-sm">
                  <i className="ri-mail-line text-accent-300"></i>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-background-50/60">Email</p>
                  <p className="break-all">info@anniversary-car-rental.com</p>
                </div>
              </a>
            </div>
          </div>

          {/* 右カラム: フォーム本体 */}
          <div className="lg:col-span-7">
            <form
              id="business-inquiry-form"
              onSubmit={handleSubmit}
              className="bg-background-50 text-foreground-950 p-8 md:p-10 rounded-sm space-y-6"
            >
              {/* Section 1: お客様情報 */}
              <div>
                <h3 className="text-xs tracking-[0.3em] uppercase text-foreground-500 mb-4 border-b border-background-200 pb-2">
                  お客様情報
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="お名前" name="name" required />
                  <Field label="フリガナ" name="furigana" required />
                </div>
                <div className="mt-5">
                  <Field label="法人名・団体名" name="organization" placeholder="イベント主催者・メディアの方はご記入ください" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                  <Field label="メールアドレス" name="email" type="email" required />
                  <Field label="電話番号" name="phone" type="tel" required />
                </div>
              </div>

              {/* Section 2: ご利用プランの選択 */}
              <div>
                <h3 className="text-xs tracking-[0.3em] uppercase text-foreground-500 mb-4 border-b border-background-200 pb-2">
                  ご利用プランの選択
                </h3>
                <fieldset className="space-y-3">
                  <legend className="text-[10px] tracking-[0.3em] uppercase text-foreground-700 mb-3">
                    ご希望のプラン <span className="text-primary-600">*</span>
                  </legend>
                  <label className="flex items-center gap-3 p-3 border border-background-300 rounded-sm cursor-pointer hover:border-foreground-400 transition-colors has-[:checked]:border-foreground-950 has-[:checked]:bg-background-100">
                    <input
                      type="radio"
                      name="plan"
                      value="イベント展示・メディア撮影プラン"
                      required
                      className="accent-foreground-950"
                    />
                    <span className="text-sm">イベント展示・メディア撮影プラン</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 border border-background-300 rounded-sm cursor-pointer hover:border-foreground-400 transition-colors has-[:checked]:border-foreground-950 has-[:checked]:bg-background-100">
                    <input
                      type="radio"
                      name="plan"
                      value="その他（協賛・タイアップのご相談など）"
                      required
                      className="accent-foreground-950"
                    />
                    <span className="text-sm">その他（協賛・タイアップのご相談など）</span>
                  </label>
                </fieldset>
              </div>

              {/* Section 3: お問い合わせ内容 */}
              <div>
                <h3 className="text-xs tracking-[0.3em] uppercase text-foreground-500 mb-4 border-b border-background-200 pb-2">
                  お問い合わせ内容
                </h3>
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-foreground-700 mb-2 block">
                    ご希望・ご質問など <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    placeholder="例：○月○日に○○で展示撮影を希望します。詳細についてご相談させてください。"
                    className="w-full px-4 py-3 border border-foreground-200 focus:border-foreground-950 focus:outline-none rounded-sm text-sm bg-background-100 placeholder:text-foreground-400 text-foreground-950 resize-none"
                  ></textarea>
                  <p className="text-[10px] text-foreground-500 mt-1 text-right">500文字以内</p>
                </div>
              </div>

              {/* ハニーポット (Tailwindで完全非表示) */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 pointer-events-none w-0 h-0"
              />

              {/* 送信ボタン */}
              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full bg-foreground-950 hover:bg-primary-600 disabled:hover:bg-foreground-950 text-background-50 py-4 text-xs tracking-[0.3em] uppercase rounded-sm whitespace-nowrap cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "loading" ? "送信中..." : "送信する"}
              </button>

              {/* 送信ステータス表示エリア */}
              {responseMessage && (
                <div
                  className={`p-4 text-xs rounded-sm border ${
                    status === "success"
                      ? "bg-accent-50 text-accent-900 border-accent-300"
                      : "bg-primary-50 text-primary-900 border-primary-300"
                  }`}
                >
                  {responseMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.3em] uppercase text-foreground-700 mb-2 block">
        {label} {required && <span className="text-primary-600">*</span>}
      </label>
      <input
        type={type}
        name={name}
        required={required}
        placeholder={placeholder}
        maxLength={100}
        className="w-full px-4 py-3 border border-foreground-200 focus:border-foreground-950 focus:outline-none rounded-sm text-sm bg-background-100 placeholder:text-foreground-400 text-foreground-950"
      />
    </div>
  );
}